<template>
  <div class="dashboard-accordion-lazy-tabs">
    <BaseAccordionDrilldown
      :items="mergedItems"
      :instance="instance"
      @panel-open="onPanelOpen"
    >
      <template v-slot:tab-content="{ tab, itemIndex, tabIndex }">
        <DashboardNodeRenderer
          v-if="isLazyRefNode(tab.children)"
          :key="`${itemIndex}-${tabIndex}-lazy`"
          :node="tab.children"
          :data-field="childDataField(itemIndex, tabIndex, tab.children, 0)"
          @row-detail="$emit('row-detail', $event)"
          @input="$emit('input', $event)"
          @filter-change="handleFilterChange"
          @update:options="handleTableOptionsChange"
          @update:reference-date="$emit('update:reference-date', $event)"
          @submit="handleFilterSubmit"
          @action="$emit('action', $event)"
        />
        <template v-else>
          <DashboardNodeRenderer
            v-for="(child, index) in (tab.children || [])"
            :key="child.id || index"
            :node="transformNode(child)"
            :data-field="childDataField(itemIndex, tabIndex, child, index)"
            @row-detail="$emit('row-detail', $event)"
            @input="$emit('input', $event)"
            @filter-change="handleFilterChange"
            @update:options="handleTableOptionsChange"
            @update:reference-date="$emit('update:reference-date', $event)"
            @submit="handleFilterSubmit"
            @action="$emit('action', $event)"
          />
        </template>
      </template>
      <template v-slot:tabs-pending="{ item, itemIndex }">
        <BaseLoadingState
          v-if="loadingByItem[itemIndex]"
          class="dashboard-accordion-lazy-tabs__loading"
        />
        <div
          v-else-if="failedByItem[itemIndex]"
          class="dashboard-accordion-lazy-tabs__error"
        >
          <span>Não foi possível carregar este conteúdo.</span>
          <button
            type="button"
            class="dashboard-accordion-lazy-tabs__retry"
            @click="fetchTabs(itemIndex, item.tabs)"
          >
            Tentar novamente
          </button>
        </div>
      </template>
    </BaseAccordionDrilldown>
    <BaseSnackbar
      :active-snackbar="feedback"
      :type-snackbar="feedbackColor"
      :text-snackbar="feedbackMessage"
      @closeSnackbar="feedback = !feedback"
    />
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import BaseAccordionDrilldown from './BaseAccordionDrilldown.vue'
import DashboardTableQueryState from '../../mixins/DashboardTableQueryState'
import { childDataField as resolveChildDataField } from './dataFieldUtils'
import BaseLoadingState from '../BaseLoadingState.vue'
import BaseSnackbar from '../BaseSnackbar'
import BaseSnackbarConfiguration from '../../mixins/BaseSnackbarConfiguration'

export default {
  name: 'DashboardAccordionLazyTabs',
  components: {
    BaseAccordionDrilldown,
    BaseLoadingState,
    BaseSnackbar,
    DashboardNodeRenderer: () => Promise.resolve(require('./DashboardNodeRenderer.vue').default)
  },
  mixins: [DashboardTableQueryState, BaseSnackbarConfiguration],
  props: {
    items: {
      type: Array,
      required: true
    },
    instance: {
      type: String,
      required: false,
      default: ''
    }
  },
  data() {
    return {
      resolvedTabsByItem: {},
      loadingByItem: {},
      failedByItem: {},
      lazyRefByItem: {},
      openItemIndex: null
    }
  },
  computed: {
    ...mapState({
      programInitiator: state => state.platformProgram.programInitiator
    }),
    mergedItems() {
      return this.items.map((item, itemIndex) => {
        if (Array.isArray(item.tabs)) {
          return item
        }
        const resolved = this.resolvedTabsByItem[itemIndex]
        return resolved ? { ...item, tabs: resolved } : item
      })
    },
    baseScreen() {
      return { type: 'accordion_drilldown', props: { items: this.mergedItems } }
    },
    isOpenItemLazy() {
      return this.openItemIndex !== null && this.openItemIndex !== undefined &&
        Boolean(this.lazyRefByItem[this.openItemIndex])
    }
  },
  methods: {
    ...mapActions(['apiGetScreenChildren']),
    onPanelOpen({ itemIndex, item }) {
      this.openItemIndex = itemIndex
      const lazyRef = this.lazyRefByItem[itemIndex] || (item && !Array.isArray(item.tabs) ? item.tabs : null)
      if (!lazyRef || this.loadingByItem[itemIndex]) {
        return
      }
      this.fetchTabs(itemIndex, lazyRef, { resetTabs: true })
    },
    async fetchTabs(itemIndex, lazyRef, { resetTabs = false } = {}) {
      this.$set(this.lazyRefByItem, itemIndex, lazyRef)
      this.$set(this.loadingByItem, itemIndex, true)
      this.$set(this.failedByItem, itemIndex, false)
      if (resetTabs) {
        this.$delete(this.resolvedTabsByItem, itemIndex)
      }
      const tableQueryRequestId = this.startTableQueryRequest()
      if (!this._tabsRequestByItem) {
        this._tabsRequestByItem = {}
      }
      this._tabsRequestByItem[itemIndex] = (this._tabsRequestByItem[itemIndex] || 0) + 1
      const itemRequestId = this._tabsRequestByItem[itemIndex]
      const isLatestItemRequest = () => itemRequestId === this._tabsRequestByItem[itemIndex]
      try {
        const result = await this.apiGetScreenChildren({
          view: this.$route && this.$route.params ? this.$route.params.view : undefined,
          programId: this.programInitiator,
          instance: lazyRef.props.instance,
          params: { ...lazyRef.props.params, ...this.tableQueryParams }
        })
        if (!isLatestItemRequest()) {
          return
        }
        const tabs = Array.isArray(result)
          ? result
          : (result && Array.isArray(result.tabs) ? result.tabs : [])
        this.$set(this.resolvedTabsByItem, itemIndex, tabs)
        this.confirmTableOptions(tableQueryRequestId)
      } catch (error) {
        if (!isLatestItemRequest()) {
          return
        }
        this.rollbackTableOptions(tableQueryRequestId)
        if (this.resolvedTabsByItem[itemIndex]) {
          this.showFeedback({ color: 'error', message: 'Não foi possível atualizar os dados. Tente novamente.' })
        } else {
          this.$set(this.failedByItem, itemIndex, true)
        }
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn(
            `DashboardAccordionLazyTabs: falha ao buscar tabs do item ${itemIndex} (instance "${lazyRef.props.instance}").`,
            error
          )
        }
      } finally {
        if (isLatestItemRequest()) {
          this.$set(this.loadingByItem, itemIndex, false)
        }
      }
    },
    performFetch() {
      const itemIndex = this.openItemIndex
      const lazyRef = this.lazyRefByItem[itemIndex]
      if (itemIndex === null || itemIndex === undefined || !lazyRef) {
        return
      }
      this.fetchTabs(itemIndex, lazyRef)
    },
    transformNode(node) {
      const overrides = this.applyNodeQueryState(node, this.transformNode)
      return overrides ? { ...node, props: overrides } : node
    },
    handleFilterChange(event) {
      if (this.isOpenItemLazy) {
        this.onFilterChange(event)
      } else {
        this.$emit('filter-change', event)
      }
    },
    handleTableOptionsChange(event) {
      if (this.isOpenItemLazy) {
        this.onTableOptionsChange(event)
      } else {
        this.$emit('update:options', event)
      }
    },
    handleFilterSubmit(event) {
      if (this.isOpenItemLazy) {
        this.onFilterSubmit(event)
      } else {
        this.$emit('submit', event)
      }
    },
    childDataField(itemIndex, tabIndex, child, index) {
      const tabNode = { type: 'tab', props: { instance: this.instance } }
      return resolveChildDataField(tabNode, child, index, `${itemIndex}-${tabIndex}`)
    },
    isLazyRefNode(value) {
      return Boolean(value) && !Array.isArray(value) && value.type === 'lazy_ref'
    }
  }
}
</script>

<style lang="scss">
.dashboard-accordion-lazy-tabs {
  &__error {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 0.8rem;
    color: var(--ds-color-black-055);
  }

  &__retry {
    cursor: pointer;
    background: none;
    border: 1px solid var(--ds-color-black-010);
    border-radius: 6px;
    padding: 4px 10px;
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--v-textPrimary-base);

    &:hover {
      background: var(--ds-color-black-005);
    }
  }
}
</style>
